'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { defaultLocale } from '@/i18n/routing';

const SUPPORTED = ['ko', 'en', 'de', 'fr', 'ja', 'vi', 'th', 'es', 'id', 'ms', 'zh-cn', 'zh-tw', 'mn'];

/**
 * 마지막 방어선 — 어디서도 못 잡은 오류.
 *
 * **어드민에서는 화면을 옮기지 않는다.** 예전에는 무조건 `/{locale}` 로 보냈는데, 그러면
 * 어드민에서 난 오류가 콘솔 홈으로 튕기면서 **무엇이 잘못됐는지 흔적도 없이 사라졌다.**
 * 2026-08-26 에 어드민 「토너먼트 개최」와 대회 상세가 500 이었는데, 화면만 보면 그냥
 * 로그인 계정 고르는 자리로 돌아온 것처럼 보여서 고장인 줄도 몰랐다.
 *
 * 오류는 **그 자리에서 말한다.** 옮겨 놓으면 사람은 자기가 뭘 잘못 눌렀나 생각하고,
 * 우리는 보고를 못 받는다. digest 는 서버 로그와 맞춰 보는 유일한 끈이라 함께 보여 준다.
 */
const GlobalError = ({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) => {
	const router = useRouter();
	const [isAdmin, setIsAdmin] = useState(false);

	useEffect(() => {
		const path = window.location.pathname;
		const admin = path === '/admin' || path.startsWith('/admin/');
		setIsAdmin(admin);
		if (admin) return;

		const browserLang = navigator.language.toLowerCase();
		const locale = SUPPORTED.includes(browserLang)
			? browserLang
			: SUPPORTED.includes(browserLang.split('-')[0])
				? browserLang.split('-')[0]
				: defaultLocale;
		router.push(`/${locale}`);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- 마운트 때 한 번만 판단한다
	}, []);

	return (
		// global-error 는 html·body 를 직접 들고 있어야 한다.
		<html lang="ko">
			<body style={{ margin: 0, fontFamily: 'system-ui, sans-serif', background: '#fafafa', color: '#171717' }}>
				<div style={{ maxWidth: 520, margin: '18vh auto', padding: '0 24px' }}>
					<h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>화면을 그리지 못했어요</h2>
					<p style={{ marginTop: 8, fontSize: 14, lineHeight: 1.7, color: '#525252' }}>
						{isAdmin
							? '이 오류는 자동으로 사라지지 않아요. 아래 코드를 개발팀에 알려 주시면 서버 로그에서 같은 건을 찾을 수 있어요.'
							: '잠시 후 다시 시도해 주세요.'}
					</p>
					{error.digest && (
						<p style={{ marginTop: 12, fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#a3a3a3' }}>
							digest: {error.digest}
						</p>
					)}
					<div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
						<button
							onClick={() => reset()}
							style={{
								height: 38,
								padding: '0 16px',
								borderRadius: 999,
								border: 'none',
								background: '#171717',
								color: '#fff',
								fontSize: 13,
								fontWeight: 700,
								cursor: 'pointer',
							}}
						>
							다시 시도
						</button>
						{isAdmin && (
							<a
								href="/admin"
								style={{
									height: 38,
									padding: '0 16px',
									display: 'inline-flex',
									alignItems: 'center',
									borderRadius: 999,
									border: '1px solid #e5e5e5',
									background: '#fff',
									color: '#525252',
									fontSize: 13,
									fontWeight: 600,
									textDecoration: 'none',
								}}
							>
								대시보드로
							</a>
						)}
					</div>
				</div>
			</body>
		</html>
	);
};

export default GlobalError;
